import styled from '@emotion/styled'
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import { useContext, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { resetPassword as resetMemberPassword } from '../../api/admin-user.api'
import { resetPassword as resetCoachPassword } from '../../api/coach.api'
import Breakpoint from '../../Breakpoint'
import Label from '../../components/form/Label'
import catchError from '../../components/global/CatchError'
import Title from '../../components/global/Title'
import { sweetAlertSuccess } from '../../components/sweetalert/Alert'
import UserContext from '../../context/UserContext'
import { useAuthCheck } from '../../hooks/useAuthCheck'
import { useLangSelected } from '../../hooks/useLangSelected'
import apiClient from '../../services/apiClient'
import { roles } from '../../types/user.type'
import { AdminForm } from './users/AdminUsersForm'

enum LoginTypeEnum {
    MEMBER = 'member',
    COACH = 'coach',
    ADMIN = 'admin',
}

const Login = () => {
    const [userConnect, setUserConnect] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)

    const loginTestId = 'login'

    const { isMember, isAdmin, isCoach, isLoading } = useAuthCheck()

    const getDefaultLoginType = () => {
        // On retourne le premier type de connexion qui n'est pas encore établi
        // Ordre de priorité : ADMIN -> MEMBER -> COACH
        if (!isMember) return LoginTypeEnum.MEMBER
        if (!isCoach) return LoginTypeEnum.COACH
        if (!isAdmin) return LoginTypeEnum.ADMIN

        // Si l'utilisateur est connecté aux trois, on retourne ADMIN par défaut
        return LoginTypeEnum.ADMIN
    }

    const [type, setType] = useState<LoginTypeEnum | undefined>(getDefaultLoginType())

    const user = useContext(UserContext)
    const navigate = useNavigate()
    const lng = useLangSelected()
    const { t } = useTranslation('translation')

    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
    const [pseudo, setPseudo] = useState('')
    const [email, setEmail] = useState('')

    const { mutate: resetMemberPasswordFn } = useMutation({
        mutationFn: (pseudo: string) => resetMemberPassword(pseudo),
    })

    const { mutate: resetCoachPasswordFn } = useMutation({
        mutationFn: (email: string) => resetCoachPassword(email),
    })

    const login = () => {
        apiClient
            .post('/users/login', { userConnect, password, type })
            .then((res) => {
                // Les données utilisateur sont maintenant directement dans res.data
                // Le token est automatiquement mis dans le cookie httpOnly par le backend
                const userData = res?.data

                if (!userData) {
                    throw new Error('No data received from login API')
                }

                // Définir le cookie activeRole pour indiquer quel rôle est actif
                let activeRole = ''
                if (userData.role === roles.ROLE_ADMIN) {
                    activeRole = 'admin'
                } else if (userData.role === roles.ROLE_MEMBER) {
                    activeRole = 'member'
                } else {
                    activeRole = 'coach'
                }
                document.cookie = `activeRole=${activeRole}; path=/; SameSite=Lax; max-age=86400` // 1 jour

                // Rafraîchir les données utilisateur dans le contexte
                user.refreshUserData?.()

                if (userData.role === roles.ROLE_ADMIN) {
                    sweetAlertSuccess(t('page.login.connection-success')).then(() => {
                        navigate(`/${lng}/bva-admin`)
                    })
                } else if (userData.role === roles.ROLE_MEMBER) {
                    sweetAlertSuccess(t('page.login.connection-success')).then(() => {
                        navigate(`/${lng}/bva-membre`)
                    })
                } else {
                    sweetAlertSuccess(t('page.login.connection-success')).then(() => {
                        navigate(`/${lng}/bva-coach`)
                    })
                }
            })
            .catch((err) => {
                catchError(err.response.data)
            })
    }

    return (
        <LoginFormContainer data-testid={`${loginTestId}-container`}>
            {showResetPasswordModal && (
                <Modal
                    centered
                    show={true}
                    onHide={() => setShowResetPasswordModal(false)}
                    data-testid={`${loginTestId}-reset-modal`}
                >
                    <Modal.Header closeButton>
                        <Modal.Title
                            id="example-custom-modal-styling-title"
                            data-testid={`${loginTestId}-reset-modal-title`}
                        >
                            {t('page.login.reset-password')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <AdminForm>
                            {type !== LoginTypeEnum.COACH && (
                                <Form.Group className="mb-3">
                                    <Label>{t('page.login.pseudo-received-by-email')}</Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('page.login.pseudo-placeholder')}
                                        onChange={(e) => setPseudo(e.target.value)}
                                        value={pseudo}
                                        data-testid={`${loginTestId}-reset-pseudo-input`}
                                    />
                                </Form.Group>
                            )}
                            {type === LoginTypeEnum.COACH && (
                                <Form.Group className="mb-3">
                                    <Label>{t('page.login.email')}</Label>
                                    <Form.Control
                                        type="email"
                                        placeholder={t('page.login.email-placeholder')}
                                        onChange={(e) => setEmail(e.target.value)}
                                        value={email}
                                        data-testid={`${loginTestId}-reset-email-input`}
                                    />
                                </Form.Group>
                            )}
                            <StyledButton
                                variant="primary"
                                type="button"
                                disabled={type === LoginTypeEnum.COACH ? !email : !pseudo}
                                onClick={() => {
                                    if (type === LoginTypeEnum.COACH) {
                                        resetCoachPasswordFn(email)
                                        setEmail('')
                                    } else {
                                        resetMemberPasswordFn(pseudo)
                                        setPseudo('')
                                    }
                                    setShowResetPasswordModal(false)
                                    sweetAlertSuccess(t('page.login.reset-password-email-sent'))
                                }}
                                data-testid={`${loginTestId}-reset-send-button`}
                            >
                                {t('page.login.send-email')}
                            </StyledButton>
                        </AdminForm>
                    </Modal.Body>
                </Modal>
            )}
            <LoginFormWrapper data-testid={`${loginTestId}-form-wrapper`}>
                <div data-testid={`${loginTestId}-title`}>
                    <TitleLogin>
                        {t('page.login.connexion')}
                        {type && (
                            <SelectCustom
                                onChange={(e) => setType(e.target.value as LoginTypeEnum)}
                                value={type}
                                data-testid={`${loginTestId}-type-select`}
                            >
                                {!isMember && <option value={LoginTypeEnum.MEMBER}>{t('page.login.member')}</option>}
                                {!isCoach && <option value={LoginTypeEnum.COACH}>{t('page.login.coach')}</option>}
                                {!isAdmin && <option value={LoginTypeEnum.ADMIN}>{t('page.login.admin')}</option>}
                            </SelectCustom>
                        )}
                    </TitleLogin>
                </div>
                <LoginForm data-testid={`${loginTestId}-form`}>
                    <RedirectButtonsContainer data-testid={`${loginTestId}-redirect-buttons`}>
                        {isMember && (
                            <StyledButton
                                variant="primary"
                                type="button"
                                onClick={() => {
                                    document.cookie = 'activeRole=member; path=/; SameSite=Lax; max-age=86400'
                                    navigate(`/${lng}/bva-membre`)
                                }}
                                data-testid={`${loginTestId}-go-member-button`}
                            >
                                {t('page.login.go-to-member-space')}
                            </StyledButton>
                        )}
                        {isCoach && (
                            <StyledButton
                                variant="primary"
                                type="button"
                                onClick={() => {
                                    document.cookie = 'activeRole=coach; path=/; SameSite=Lax; max-age=86400'
                                    navigate(`/${lng}/bva-coach`)
                                }}
                                data-testid={`${loginTestId}-go-coach-button`}
                            >
                                {t('page.login.go-to-coach-space')}
                            </StyledButton>
                        )}
                        {isAdmin && (
                            <StyledButton
                                variant="primary"
                                type="button"
                                onClick={() => {
                                    document.cookie = 'activeRole=admin; path=/; SameSite=Lax; max-age=86400'
                                    navigate(`/${lng}/bva-admin`)
                                }}
                                data-testid={`${loginTestId}-go-admin-button`}
                            >
                                {t('page.login.go-to-admin-space')}
                            </StyledButton>
                        )}
                    </RedirectButtonsContainer>

                    {type && (
                        <>
                            {type === LoginTypeEnum.COACH ? (
                                <Form.Group className="mb-3" controlId="formBasicEmail">
                                    <Label>{t('page.login.email-label')}</Label>
                                    <Form.Control
                                        type="email"
                                        placeholder={t('page.login.enter-email')}
                                        onChange={(e) => setUserConnect(e.target.value)}
                                        value={userConnect}
                                        data-testid={`${loginTestId}-email-input`}
                                    />
                                </Form.Group>
                            ) : (
                                <Form.Group className="mb-3" controlId="formBasicPseudo">
                                    <Label>{t('page.login.pseudo')}</Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('page.login.enter-pseudo')}
                                        onChange={(e) => setUserConnect(e.target.value)}
                                        value={userConnect}
                                        data-testid={`${loginTestId}-pseudo-input`}
                                    />
                                </Form.Group>
                            )}
                            <Form.Group className="mb-3" controlId="formBasicPassword">
                                <Label>{t('page.login.password')}</Label>
                                <PasswordInputContainer>
                                    <Form.Control
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder={t('page.login.enter-password')}
                                        onChange={(e) => setPassword(e.target.value)}
                                        value={password}
                                        data-testid={`${loginTestId}-password-input`}
                                    />
                                    <PasswordToggleButton
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={
                                            showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
                                        }
                                        data-testid={`${loginTestId}-password-toggle-button`}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </PasswordToggleButton>
                                </PasswordInputContainer>
                            </Form.Group>
                            <StyledLinkAByButton
                                variant="link"
                                type="button"
                                onClick={() => setShowResetPasswordModal(true)}
                                data-testid={`${loginTestId}-forgot-password-link`}
                            >
                                {t('page.login.forgot-password')}
                            </StyledLinkAByButton>
                            <StyledButton
                                variant="primary"
                                type="button"
                                disabled={!userConnect || !password}
                                onClick={() => login()}
                                data-testid={`${loginTestId}-connect-button`}
                            >
                                {t('page.login.connect')}
                            </StyledButton>
                        </>
                    )}
                </LoginForm>
            </LoginFormWrapper>
        </LoginFormContainer>
    )
}

const SelectCustom = styled(Form.Select)`
    cursor: pointer;
    margin-left: 8px;
    font-size: 1.6rem;
`

const TitleLogin = styled(Title)`
    display: flex;
`

const StyledLinkAByButton = styled(Button)`
    display: flex;
    margin: 8px 0;
    padding: 0;
    font-size: 1.6rem;
`

export const StyledButton = styled(Button)`
    font-size: 1.6rem;

    &:active {
        transform: scale(0.95);
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`

const LoginForm = styled(Form)`
    width: 100%;

    input {
        font-size: 1.6rem;
    }
`

const LoginFormContainer = styled.div`
    display: flex;
    width: 100%;
    height: calc(100vh - 166px);
    justify-content: center;
    align-items: center;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        padding: 16px;
    }
`

const LoginFormWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 35%;
    border-radius: 6px;
    padding: 24px;
    background-color: white;
    justify-content: center;
    align-items: center;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        width: 100%;
    }
`

const RedirectButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
    width: 100%;
`

const PasswordInputContainer = styled.div`
    position: relative;
    width: 100%;
`

const PasswordToggleButton = styled.button`
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.6rem;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    transition: color 0.2s ease;

    &:hover {
        color: var(--primary-color, #b40000);
    }

    &:focus {
        outline: 2px solid var(--primary-color, #b40000);
        outline-offset: 2px;
        border-radius: 4px;
    }
`

export default Login
